/* eslint-disable */
import React from 'react';
import { Checkbox } from '@walmart-dataventures/shared-components';
import { styled } from '@ldm/theme';
import { Icon } from '@ldm/icons';
import Typography from '@mui/material/Typography';
import { LevelDataMap } from './types';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { IconButton } from '@mui/material';
import { BOGLE_REGULAR } from '../../constants/constant';
import { hasAncestor, hasItem } from './util';
import { defaultL10nStrings } from './constants';

const Collapse = styled.div<{ show: boolean }>`
	display: ${(props) => (props.show ? 'block' : 'none')};
`;

type HierarchyListProps = {
	levelDataMaps: LevelDataMap[];
	level: number;
	header: string;
	noRollup: boolean;
	onSelectionChange: (a, b, checked: boolean) => void;
	onSubHierarchyExpandCollapse: (
		parentLevel: number,
		parentNestedId: string
	) => void;
	onExpand: (level: number, id: string) => void;
	onSelectAll: (checked: boolean) => void;
	focusedItems: LevelDataMap[];
	indeterminateItems: LevelDataMap[];
	selectedItems: LevelDataMap[];
	expandedItems: LevelDataMap[];
	l10nStrings?: typeof defaultL10nStrings;
};

const HierarchyList: React.FC<HierarchyListProps> = (props) => {
	const {
		header = '',
		levelDataMaps,
		level = -1,
		noRollup = false,
		onSelectAll,
		onExpand,
		onSelectionChange,
		onSubHierarchyExpandCollapse,
		focusedItems,
		indeterminateItems,
		selectedItems,
		expandedItems,
		l10nStrings = defaultL10nStrings,
	} = props;

	const getIsChecked = (item) =>
		hasItem(selectedItems, item)
			? true
			: !noRollup && hasAncestor(selectedItems, item, levelDataMaps);

	const handleOnChange = (event) => {
		if (onSelectionChange) {
			const [lvl, nestedId] = event.target.id.split('~');
			onSelectionChange(lvl, nestedId, event.target.checked);
		}
	};

	const handleOnExpand = (itemLevel, itemNestedId) => {
		const currentItem = levelDataMaps[itemLevel]?.get(itemNestedId);
		if (currentItem && !currentItem.disabled && onExpand) {
			onExpand(itemLevel, itemNestedId);
		}
	};

	const handleSelectAll = (event) => {
		if (onSelectAll) {
			onSelectAll(event.target.checked);
		}
	};

	const getParentItems = () => {
		const parentLevel = level - 1;
		// Use the levelDataMaps data to maintain consistent ordering
		return [...levelDataMaps[parentLevel]?.values()].filter(
			// Filter out parent items without children, since they won't be rendered in the child menu
			(pItem) =>
				pItem.subHierarchy.length > 0 &&
				// Include the selected, focused and indeterminate parent items,
				// since all should be rendered in the list.
				(hasItem(selectedItems, pItem) ||
					hasItem(focusedItems, pItem) ||
					hasItem(indeterminateItems, pItem))
		);
	};

	const renderLevelHeader = () => {
		const curLevelItems =
			level < 1
				? [...levelDataMaps[level]?.values()]
				: getParentItems().flatMap((item) => item.subHierarchy);

		if (curLevelItems.length < 1) {
			return null;
		}

		const checkedItems = curLevelItems.filter((item) => {
			return (
				hasItem(selectedItems, item) ||
				(!noRollup && hasAncestor(selectedItems, item, levelDataMaps))
			);
		});
		const indeterminates = curLevelItems.filter((item) =>
			hasItem(indeterminateItems, item)
		);
		const isIndeterminate =
			indeterminates.length > 0 ||
			(checkedItems.length > 0 && checkedItems.length < curLevelItems.length);

		return (
			<div className={`levelHeaderStyled`}>
				<span
					className={`selectAllCheckboxWrapper`}
					data-testid={`Hierarchy_CompleteLevelSelector_${level}`}>
					<Checkbox
						className={'levelHeaderCheckbox'}
						actionable
						checked={checkedItems.length > 0}
						indeterminate={isIndeterminate}
						id={`level${level}`}
						label={`${header} ${l10nStrings.renderSelectionCount(
							checkedItems.length,
							curLevelItems.length
						)}`}
						onChange={handleSelectAll}
					/>
				</span>
			</div>
		);
	};

	const renderSelectionItem = (item) => {
		const header = `${item.id} ${item.name}`.toLowerCase();
		const checked = getIsChecked(item);
		const indeterminate = !noRollup && hasItem(indeterminateItems, item);
		// Note: checkedOrIndeterminate only affects the styling
		const checkedOrIndeterminate = checked || indeterminate;

		return (
			<div
				className={`selectionItem selectionListItem`}
				key={item.nestedId}
				checked={checkedOrIndeterminate}
				disabled={item.disabled}
				id={item.nestedId}
				onClick={() => handleOnExpand(item.level, item.nestedId)}
				role="button"
				tabIndex={0}>
				<div className={`listItemLeft`}>
					<div className={`checkboxWrapper`}>
						<span
							data-testid={`Hierarchy_CheckboxSelector_${item.level}~${item.nestedId}`}>
							<Checkbox
								className="selectionItemCheckbox"
								actionable
								checked={checked}
								disabled={item.disabled}
								indeterminate={indeterminate}
								value={`${item.nestedId}-${item.name}`}
								a11yLabelledBy={`${item.level}~${item.nestedId}`}
								id={`${item.level}~${item.nestedId}`}
								onChange={handleOnChange}
							/>
						</span>

						<Typography
							sx={{
								marginLeft: '9px',
								whiteSpace: 'nowrap',
								textOverflow: 'ellipsis',
								overflow: 'hidden',
								fontFamily: BOGLE_REGULAR,
								fontSize: '14px',
							}}
							disabled={item.disabled}
							// role="button"
							// tabIndex={0}
						>
							<span
								className={`itemNameText`}
								title={`${item.id} ${item.name}`}>
								{header}{' '}
							</span>
						</Typography>
					</div>
				</div>
				<div className={`listItemRight`}>
					{item.subHierarchy.length < 1 ? null : (
						<IconButton
							className={`iconButton`}
							data-testid={`Hierarchy_Expand_${item.level}~${item.nestedId}`}
							id={`expand-${item.nestedId}`}>
							<ChevronRightIcon />
						</IconButton>
					)}
				</div>
			</div>
		);
	};

	const renderLevel0 = () => {
		const level0Items = Array.from(levelDataMaps[level]?.values() || []);
		return (
			<div className={`itemsContainer`} data-testid="Hierarchy_ListContainer">
				<div className={`itemList`}>
					{level0Items?.map((item) => renderSelectionItem(item))}
				</div>
			</div>
		);
	};

	const getSubHierarchyHeader = (parentItem) => {
		if (parentItem.subHierarchy.length < 1) {
			return '';
		}
		const checkedItems = parentItem.subHierarchy.filter((item) => {
			return (
				hasItem(selectedItems, item) ||
				(!noRollup && hasAncestor(selectedItems, item, levelDataMaps))
			);
		});

		return ` ${l10nStrings.renderSelectionCount(
			checkedItems.length,
			parentItem.subHierarchy.length
		)}`;
	};

	const handleSubHierarchyExpandCollapse = (
		parentLevel: number,
		parentNestedId: string
	) => {
		if (onSubHierarchyExpandCollapse) {
			onSubHierarchyExpandCollapse(parentLevel, parentNestedId);
		}
	};

	const renderSubHierarchyHeader = (parentItem) => {
		const header = `${parentItem?.id} ${parentItem?.name}`.toLowerCase();
		const isExpanded = expandedItems[level - 1]?.has(parentItem.nestedId);

		return (
			<div
				className={`subHierarchy subHierachyList`}
				data-value={parentItem.nestedId}
				id={`collapse-${parentItem.nestedId}`}
				role="button"
				onClick={() =>
					handleSubHierarchyExpandCollapse(
						parentItem.level,
						parentItem.nestedId
					)
				}>
				<div className={`subHeaderContainer subList`}>
					<Typography
						sx={{
							whiteSpace: 'nowrap',
							textOverflow: 'ellipsis',
							overflow: 'hidden',
						}}>
						<span className={`subHeaderText`}>{header}</span>

						<span className={`subHeaderCal`}>
							{getSubHierarchyHeader(parentItem)}
						</span>
					</Typography>
					{isExpanded ? (
						<ArrowDropDownIcon
							sx={{
								color: 'rgba(0, 0, 0, 0.54)',
							}}
						/>
					) : (
						<ArrowRightIcon
							sx={{
								color: 'rgba(0, 0, 0, 0.54)',
							}}
						/>
					)}
				</div>
			</div>
		);
	};

	const renderLevel0Plus = () => {
		const parentItems = getParentItems();
		if (parentItems.length < 1) return null;

		return (
			<div className={`itemsContainer`}>
				<div className={`itemList`}>
					{parentItems.map((pItem) => (
						<div key={pItem.nestedId}>
							{renderSubHierarchyHeader(pItem)}
							<Collapse show={expandedItems[level - 1]?.has(pItem.nestedId)}>
								<div className={`selectionItemList`}>
									{pItem.subHierarchy?.map(renderSelectionItem)}
								</div>
							</Collapse>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderList = () => (
		<div className={`listContainer listColumn`}>
			{renderLevelHeader()}
			{level === 0 ? renderLevel0() : renderLevel0Plus()}
		</div>
	);

	return !levelDataMaps || !levelDataMaps[level] ? (
		<div>Loading...</div>
	) : (
		renderList()
	);
};

export default HierarchyList;
